import { InfluxFilters, isWindowFunction } from '../influx.service';

export type Scalar = string | number;
export interface ScalarObject {
  [key: string]: Scalar;
}

export type MetadataType = 'misonet';

export abstract class InfluxMetadataService {
  /**
   * Used to add virtual fields that don't exist in the source data.
   * The keys are the server side representation of the field, the values are the client side representation.
   */
  protected abstract get specialFields(): Map<string, Map<Scalar, Scalar>>;
  protected abstract load(filters: ScalarObject): Promise<ScalarObject[]>;

  public async resolveTimelineFilters(filters: InfluxFilters): Promise<InfluxFilters> {
    return filters;
  }

  public resolveWindowFunction(name: string): InfluxFilters['_windowFunction'] | undefined {
    if (isWindowFunction(name)) {
      return name;
    }

    console.warn(`Invalid influx window function "${name}", defaulting to "mean".`);
    return 'mean';
  }

  public async getDistinctValues(field: string, filters: ScalarObject): Promise<Set<Scalar>> {
    if (this.specialFields.has(field)) {
      return new Set(this.specialFields.get(field).values());
    }

    const data = await this.load(filters);
    return new Set(
      data
        .filter(row => Object.entries(filters).every(([key, value]) => this.specialFields.has(key) || row[key] === value))
        .map(row => row[field])
    );
  }

  public async getDistinctValuesWithLabel(field: string, labelField: string, filters: ScalarObject): Promise<Map<Scalar, Scalar>> {
    if (field === labelField && this.specialFields.has(field)) {
      return new Map(Array.from(this.specialFields.get(field).values()).map(value => [value, value]));
    }

    const data = await this.load(filters);
    return new Map(
      data
        .filter(row => Object.entries(filters).every(([key, value]) => this.specialFields.has(key) || row[key] === value))
        .map(row => [row[field], row[labelField]])
    );
  }
}
